/**
 * Secure Storage Utility with Expiration
 * 
 * Uses localStorage with automatic expiration checking
 * - Data persists across browser sessions
 * - Automatically expires after set duration
 * - More secure than permanent storage
 */

// Storage configuration
const STORAGE_CONFIG = {
  // Session duration in milliseconds
  SESSION_DURATION: 8 * 60 * 60 * 1000, // 8 hours
  // Check expiration on every read
  CHECK_EXPIRATION: true,
};

// In-memory storage for highly sensitive data (cleared on page reload)
const memoryStorage = {};

/**
 * Storage Types
 */
export const StorageType = {
  LOCAL: 'local',        // localStorage with expiration
  MEMORY: 'memory',      // In-memory - cleared on page reload
  NONE: 'none'          // Don't store (for httpOnly cookies)
};

/**
 * Storage Keys Configuration
 */
export const StorageKeys = {
  // User data with expiration
  USER_DATA: 'user',
  DEVICE_ID: 'ds',
  DEVICE_ADMIN: 'da',
  API_BASE_URL: 'api_base_url',
  API_REGION: 'api_region',
  
  // Expiration tracking
  USER_EXPIRY: 'user_expiry',
  
  // Memory only (cleared on reload)
  TEMP_TOKEN: 'temp_token',
  OTP_EMAIL: 'otp_email',
};

/**
 * Get current timestamp
 */
const getCurrentTimestamp = () => Date.now();

/**
 * Check if item has expired
 */
const isExpired = (expiryTimestamp) => {
  if (!expiryTimestamp) return false;
  return getCurrentTimestamp() > expiryTimestamp;
};

/**
 * Get item from storage with expiration check
 */
export const getSecureItem = (key, storageType = StorageType.LOCAL) => {
  try {
    let value;
    
    switch (storageType) {
      case StorageType.LOCAL:
        // Check if user data has expired
        if (key === StorageKeys.USER_DATA && STORAGE_CONFIG.CHECK_EXPIRATION) {
          const expiryStr = localStorage.getItem(StorageKeys.USER_EXPIRY);
          if (expiryStr && isExpired(parseInt(expiryStr))) {
            console.warn('🔒 Session expired. Clearing user data...');
            clearUserSession();
            return null;
          }
        }
        
        value = localStorage.getItem(key);
        break;
        
      case StorageType.MEMORY:
        value = memoryStorage[key];
        break;
        
      default:
        return null;
    }
    
    if (!value) return null;
    
    // Try to parse JSON, return raw string if fails
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  } catch (error) {
    console.error(`Error reading from ${storageType} storage:`, error);
    return null;
  }
};

/**
 * Set item in storage with automatic expiration
 */
export const setSecureItem = (key, value, storageType = StorageType.LOCAL, customDuration = null) => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    
    switch (storageType) {
      case StorageType.LOCAL:
        localStorage.setItem(key, stringValue);
        
        // Set expiration for user data
        if (key === StorageKeys.USER_DATA) {
          const duration = customDuration || STORAGE_CONFIG.SESSION_DURATION;
          const expiryTime = getCurrentTimestamp() + duration;
          localStorage.setItem(StorageKeys.USER_EXPIRY, expiryTime.toString());
          console.log(`✅ Session will expire in ${duration / (60 * 60 * 1000)} hours`);
        }
        break;
        
      case StorageType.MEMORY:
        memoryStorage[key] = stringValue;
        break;
        
      default:
        break;
    }
    
    return true;
  } catch (error) {
    console.error(`Error writing to ${storageType} storage:`, error);
    return false;
  }
};

/**
 * Remove item from storage
 */
export const removeSecureItem = (key, storageType = StorageType.LOCAL) => {
  try {
    switch (storageType) {
      case StorageType.LOCAL:
        localStorage.removeItem(key);
        break;
      case StorageType.MEMORY:
        delete memoryStorage[key];
        break;
      default:
        break;
    }
    return true;
  } catch (error) {
    console.error(`Error removing from ${storageType} storage:`, error);
    return false;
  }
};

/**
 * Clear all secure storage
 */
export const clearSecureStorage = (includeMemory = true) => {
  try {
    localStorage.clear();
    if (includeMemory) {
      Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]);
    }
    return true;
  } catch (error) {
    console.error('Error clearing storage:', error);
    return false;
  }
};

/**
 * Clear user session (logout)
 */
export const clearUserSession = () => {
  try {
    localStorage.removeItem(StorageKeys.USER_DATA);
    localStorage.removeItem(StorageKeys.USER_EXPIRY);
    localStorage.removeItem(StorageKeys.DEVICE_ID);
    localStorage.removeItem(StorageKeys.DEVICE_ADMIN);
    console.log('✅ User session cleared');
    return true;
  } catch (error) {
    console.error('Error clearing user session:', error);
    return false;
  }
};

/**
 * Check if session is still valid (not expired)
 */
export const isSessionValid = () => {
  const userData = getSecureItem(StorageKeys.USER_DATA);
  if (!userData || !userData.jwt) return false;
  
  // Check expiration
  const expiryStr = localStorage.getItem(StorageKeys.USER_EXPIRY);
  if (expiryStr && isExpired(parseInt(expiryStr))) {
    return false;
  }
  
  return true;
};

/**
 * Get user JWT token if session is valid
 */
export const getUserToken = () => {
  if (!isSessionValid()) return null;
  const userData = getSecureItem(StorageKeys.USER_DATA);
  return userData?.jwt || null;
};

/**
 * Get user data without token
 */
export const getUserData = () => {
  const userData = getSecureItem(StorageKeys.USER_DATA);
  if (!userData) return null;
  
  // Return user data without exposing JWT
  const { jwt, ...safeUserData } = userData;
  return safeUserData;
};

/**
 * Set user session
 */
export const setUserSession = (userData) => {
  return setSecureItem(StorageKeys.USER_DATA, userData);
};

/**
 * Storage size monitor (for debugging)
 */
export const getStorageSize = () => {
  let localSize = 0;
  let memorySize = 0;
  
  try {
    // Calculate localStorage size
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        localSize += localStorage[key].length + key.length;
      }
    }
    
    // Calculate memory storage size
    for (let key in memoryStorage) {
      memorySize += memoryStorage[key].length + key.length;
    }
    
    return {
      local: `${(localSize / 1024).toFixed(2)} KB`,
      memory: `${(memorySize / 1024).toFixed(2)} KB`,
      total: `${((localSize + memorySize) / 1024).toFixed(2)} KB`
    };
  } catch (error) {
    return { error: 'Unable to calculate storage size' };
  }
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  get: getSecureItem,
  set: setSecureItem,
  remove: removeSecureItem,
  clear: clearSecureStorage,
  isValid: isSessionValid,
  getToken: getUserToken,
  getUser: getUserData,
  setUser: setUserSession,
  clearUser: clearUserSession,
  getSize: getStorageSize,
  StorageType,
  StorageKeys
};
